import { useMemo, useState } from "react";
import { Cpu, Zap, Thermometer, Signal, Gauge, AlertTriangle, WifiOff, CircleDot, Loader2, Move } from "lucide-react";

const ACCENT = "#3DA935"; // verde logo Tigo
const n = (v, d = 1) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: d }));

// colore del modulo in base allo stato + intensità di produzione (0→1 sul Wp nominale)
function cellStyle(m, wp) {
  if (m.status === "offline") return { background: "#1f2937", border: "1px solid #374151", color: "#6b7280" };
  if (m.status === "allarme") return { background: "#7f1d1d", border: "1px solid #ef4444", color: "#fecaca" };
  const t = Math.max(0, Math.min(1, (m.pin_w || 0) / (wp || 440)));
  const bg = `rgba(61,169,53,${0.16 + 0.72 * t})`;
  return { background: bg, border: "1px solid rgba(61,169,53,.4)", color: t > 0.45 ? "#fff" : "#bbf7d0" };
}

const StatusIcon = ({ s }) => (
  s === "offline" ? <WifiOff className="h-3.5 w-3.5 text-neutral-500" />
    : s === "allarme" ? <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
      : <CircleDot className="h-3.5 w-3.5 text-emerald-400" />
);

function Metric({ icon: Icon, label, value, unit }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-2">
      <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400"><Icon className="h-3 w-3" />{label}</span>
      <span className="font-mono text-base font-bold text-white">{value}<small className="ml-0.5 text-xs font-medium text-neutral-400">{unit}</small></span>
    </div>
  );
}

function ModuleCell({ m, wp, selected, onClick, draggable, onDragStart, onDragOver, onDrop, onDragEnd }) {
  return (
    <button type="button" onClick={onClick} data-testid={`tigo-mod-${m.idx}`}
      draggable={draggable} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd}
      title={`${m.etichetta} · ${m.pin_w} W`}
      className={`flex aspect-[4/5] flex-col items-center justify-center rounded-md text-[10px] font-bold leading-none transition-transform hover:scale-[1.06] ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${selected ? "ring-2 ring-offset-1 ring-offset-neutral-950" : ""}`}
      style={{ ...cellStyle(m, wp), gridColumn: m.col + 1, gridRow: m.row + 1, ...(selected ? { "--tw-ring-color": ACCENT } : {}) }}>
      <span>{m.etichetta}</span>
      <span className="mt-0.5 font-mono text-[9px] opacity-90">{m.status === "offline" ? "—" : `${m.pin_w}W`}</span>
    </button>
  );
}

// Griglia interattiva dei moduli + pannello dati del modulo selezionato + elenco dispositivi.
// In modalità `editing` i moduli sono trascinabili per riordinare la disposizione.
export function TigoLayout({ data, loading, editing = false, editPos = null, onMovePanel }) {
  const [sel, setSel] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const mods = data?.moduli || [];
  const wp = data?.module_wp;
  const selMod = useMemo(() => mods.find((m) => m.idx === sel) || null, [mods, sel]);

  // in editing: posizioni dai controlli; fuori editing: posizioni reali dei moduli
  const placed = useMemo(() => {
    if (editing && editPos) return mods.map((m) => ({ ...m, row: editPos[m.idx]?.row ?? m.row, col: editPos[m.idx]?.col ?? m.col }));
    return mods;
  }, [mods, editing, editPos]);

  const { cols, rows } = useMemo(() => {
    let mc = 0, mr = 0;
    placed.forEach((m) => { mc = Math.max(mc, m.col); mr = Math.max(mr, m.row); });
    const extra = editing ? 1 : 0; // spazio extra per spostare in editing
    return { cols: mc + 1 + extra, rows: mr + 1 + extra };
  }, [placed, editing]);

  const posMap = useMemo(() => {
    const o = {};
    placed.forEach((m) => { o[`${m.row}:${m.col}`] = m; });
    return o;
  }, [placed]);

  if (loading && !data) return <div className="flex items-center gap-2 p-8 text-sm text-neutral-400"><Loader2 className="h-5 w-5 animate-spin" />Lettura ottimizzatori…</div>;
  if (!data) return <div className="p-8 text-center text-sm text-neutral-400">Nessun dato disponibile.</div>;

  const dropAt = (r, c) => (e) => {
    e.preventDefault();
    if (dragIdx != null && onMovePanel) onMovePanel(dragIdx, r, c);
    setDragIdx(null);
  };

  return (
    <div className="space-y-4" data-testid="tigo-layout">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric icon={Cpu} label="Ottimizzatori" value={data.ottimizzatori} unit="" />
        <Metric icon={Zap} label="Potenza DC" value={n(data.potenza_dc_kw, 2)} unit="kW" />
        <Metric icon={Signal} label="Online" value={`${data.online}/${data.ottimizzatori}`} unit="" />
        <Metric icon={AlertTriangle} label="Allarmi" value={data.allarme + data.offline} unit="" />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-widest text-neutral-400">Layout campo FV · {data.model}</span>
          {editing
            ? <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: ACCENT }}><Move className="h-3.5 w-3.5" />Trascina i pannelli per riordinarli</span>
            : <span className="text-[11px] text-neutral-500">{data.notte ? "notte" : "aggiornato " + data.aggiornato}</span>}
        </div>
        <div className="overflow-x-auto rounded-xl border border-neutral-800 bg-neutral-950 p-3">
          {editing ? (
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(26px, 1fr))`, gridTemplateRows: `repeat(${rows}, auto)` }} data-testid="tigo-grid-edit">
              {Array.from({ length: rows * cols }).map((_, i) => {
                const r = Math.floor(i / cols), c = i % cols;
                const m = posMap[`${r}:${c}`];
                if (m) return <ModuleCell key={m.idx} m={m} wp={wp} selected={false} draggable
                  onDragStart={() => setDragIdx(m.idx)} onDragOver={(e) => e.preventDefault()} onDrop={dropAt(r, c)}
                  onDragEnd={() => setDragIdx(null)} onClick={() => {}} />;
                return <div key={`e${r}-${c}`} onDragOver={(e) => e.preventDefault()} onDrop={dropAt(r, c)} data-testid={`tigo-cell-${r}-${c}`}
                  className="aspect-[4/5] rounded-md border border-dashed border-neutral-800 bg-neutral-900/30" style={{ gridColumn: c + 1, gridRow: r + 1 }} />;
              })}
            </div>
          ) : (
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(26px, 1fr))` }} data-testid="tigo-grid">
              {placed.map((m) => (
                <ModuleCell key={m.idx} m={m} wp={wp} selected={sel === m.idx} onClick={() => setSel(m.idx)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {!editing && selMod && (
        <div className="rounded-xl border p-3" style={{ borderColor: `${ACCENT}55`, background: `${ACCENT}0d` }} data-testid="tigo-mod-detail">
          <div className="mb-2 flex items-center gap-2">
            <StatusIcon s={selMod.status} />
            <span className="text-sm font-bold text-white">Modulo {selMod.etichetta}</span>
            <span className="font-mono text-[11px] text-neutral-400">S/N {selMod.serial}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Metric icon={Zap} label="Potenza" value={selMod.pin_w} unit="W" />
            <Metric icon={Gauge} label="Vin" value={n(selMod.vin_v, 1)} unit="V" />
            <Metric icon={Gauge} label="Vout" value={n(selMod.vout_v, 1)} unit="V" />
            <Metric icon={Zap} label="Corrente" value={n(selMod.iin_a, 2)} unit="A" />
            <Metric icon={Thermometer} label="Temp." value={n(selMod.temp_c, 1)} unit="°C" />
            <Metric icon={Signal} label="Segnale" value={selMod.rssi_pct} unit="%" />
          </div>
        </div>
      )}

      {!editing && (
      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-neutral-400">Elenco dispositivi</div>
        <div className="overflow-x-auto rounded-xl border border-neutral-800">
          <table className="w-full text-left text-xs" data-testid="tigo-device-list">
            <thead className="bg-neutral-900 text-[10px] uppercase tracking-wide text-neutral-400">
              <tr>
                <th className="px-3 py-2">Modulo</th><th className="px-3 py-2">Seriale</th><th className="px-3 py-2">Stato</th>
                <th className="px-3 py-2 text-right">P (W)</th><th className="px-3 py-2 text-right">Vin</th>
                <th className="px-3 py-2 text-right">Iin</th><th className="px-3 py-2 text-right">Temp</th><th className="px-3 py-2 text-right">Segnale</th>
              </tr>
            </thead>
            <tbody>
              {mods.map((m) => (
                <tr key={m.idx} onClick={() => setSel(m.idx)} data-testid={`tigo-row-${m.idx}`}
                  className={`cursor-pointer border-t border-neutral-800 transition-colors hover:bg-neutral-900 ${sel === m.idx ? "bg-neutral-900" : ""}`}>
                  <td className="px-3 py-1.5 font-bold text-white">{m.etichetta}</td>
                  <td className="px-3 py-1.5 font-mono text-neutral-400">{m.serial}</td>
                  <td className="px-3 py-1.5"><span className="inline-flex items-center gap-1 text-neutral-300"><StatusIcon s={m.status} />{m.status}</span></td>
                  <td className="px-3 py-1.5 text-right font-mono text-white">{m.pin_w}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-neutral-300">{n(m.vin_v, 1)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-neutral-300">{n(m.iin_a, 2)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-neutral-300">{n(m.temp_c, 0)}°</td>
                  <td className="px-3 py-1.5 text-right font-mono text-neutral-300">{m.rssi_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}
